import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useSessions, useDeleteSession } from '@/hooks/useSessions';
import { useAuth } from '@/auth/AuthContext';
import { isAdmin } from '@/types';
import { SessionBadge, VerdictBadge } from '@/components/common/StatusBadge';
import { Loading, ErrorBanner, EmptyState } from '@/components/common/States';
import { relTime, fmtDuration, fmtMbps, shortId } from '@/lib/format';

export function Sessions() {
  const { data, isLoading, error } = useSessions();
  const { user: me } = useAuth();
  const delM = useDeleteSession();
  const nav = useNavigate();
  const [sel, setSel] = useState<Set<string>>(new Set());
  const canDelete = isAdmin(me) || me?.can_delete_sessions === true;

  const toggle = (id: string) =>
    setSel((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const compare = () => {
    if (sel.size >= 2) nav(`/sessions/compare?ids=${[...sel].join(',')}`);
  };

  return (
    <div className="fade-up">
      <div className="page-head spread">
        <div>
          <div className="eyebrow">history</div>
          <div className="title">Sessions</div>
          <div className="sub">Every traffic test, newest first. Tick two or more to compare.</div>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <Link to="/regression" className="btn btn-ghost">↗ Regression</Link>
          <button className="btn" onClick={compare} disabled={sel.size < 2}>
            ⇄ Compare{sel.size >= 2 ? ` (${sel.size})` : ''}
          </button>
          <Link to="/sessions/new" className="btn btn-primary">+ New test</Link>
        </div>
      </div>

      {isLoading ? (
        <Loading label="Loading sessions" />
      ) : error ? (
        <ErrorBanner error={error} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          title="No tests yet"
          hint="Configure and launch a traffic test against your agents."
          action={<Link to="/sessions/new" className="btn btn-primary">+ New test</Link>}
        />
      ) : (
        <div className="panel">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th>Session</th>
                <th>Owner</th>
                <th>Verdict</th>
                <th>Firmware / tag</th>
                <th>Status</th>
                <th>Peak RX</th>
                <th>Duration</th>
                <th>Started</th>
                {canDelete && <th className="right"></th>}
              </tr>
            </thead>
            <tbody>
              {data.map((s) => (
                <tr key={s.id}>
                  <td>
                    <input type="checkbox" checked={sel.has(s.id)} onChange={() => toggle(s.id)} />
                  </td>
                  <td>
                    <Link to={`/sessions/${s.id}`} className="mono">
                      {s.label || shortId(s.id)}
                    </Link>
                    {s.error_reason && (
                      <div className="mono" style={{ fontSize: 11, color: 'var(--loss)' }}>
                        {s.error_reason}
                      </div>
                    )}
                  </td>
                  <td className="mono dim" style={{ fontSize: 12 }} title={s.created_by_email ?? undefined}>
                    {s.created_by_email || s.created_by_name || '—'}
                  </td>
                  <td>{s.verdict ? <VerdictBadge verdict={s.verdict} /> : <span className="dim">—</span>}</td>
                  <td className="mono dim" style={{ fontSize: 12 }}>
                    {s.firmware || '—'}{s.tag ? ` · #${s.tag}` : ''}
                  </td>
                  <td><SessionBadge status={s.status} /></td>
                  <td className="mono signal">{fmtMbps(s.summary_json?.rx_max_mbps ?? null)}</td>
                  <td className="mono">{fmtDuration(s.duration_seconds)}</td>
                  <td className="dim">{s.started_at ? relTime(s.started_at) : '—'}</td>
                  {canDelete && (
                    <td className="right">
                      <button
                        className="btn btn-ghost"
                        style={{ color: 'var(--loss)', padding: '4px 8px' }}
                        disabled={s.status === 'active' || (delM.isPending && delM.variables === s.id)}
                        title={s.status === 'active' ? 'stop the session before deleting' : 'delete session'}
                        onClick={() => {
                          if (s.status === 'active') return;
                          if (!window.confirm(`Delete session ${s.label || shortId(s.id)}? Its metrics are permanently removed.`)) return;
                          delM.mutate(s.id);
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
